import { BlogPost } from '@/types/blog'
import { Button } from '@/components/ui/button'
import { MessageCircle, Info } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const whatsappUrl =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20comparativo%20sobre%20aulas%20a%20domic%C3%ADlio%20e%20gostaria%20de%20uma%20cota%C3%A7%C3%A3o%20para%20minha%20regi%C3%A3o%21'

export const postAulaDomicilio: BlogPost = {
  slug: 'aula-futebol-domicilio-vale-a-pena',
  title: 'Aula de Futebol a Domicílio Vale a Pena? Vantagens vs Escolinha',
  excerpt:
    'Analisamos os prós e contras de contratar um professor particular de futebol versus matricular seu filho em uma escolinha tradicional.',
  date: '16 Fev, 2026',
  author: 'Equipe SuperFut',
  category: 'Dicas para Pais',
  location: 'São Paulo, SP',
  image:
    'https://img.usecurling.com/p/800/400?q=soccer%20coach%20condo%20garden',
  metaDescription:
    'Compare aula de futebol a domicílio com escolinha tradicional. Vantagens, desvantagens e preços.',
  faq: [
    {
      question: 'É mais caro que a escolinha?',
      answer:
        'O investimento costuma ser um pouco maior devido à exclusividade e deslocamento do professor, mas o custo-benefício se equilibra ao economizar tempo e combustível.',
    },
    {
      question: 'Preciso ter quadra oficial?',
      answer:
        'Não. Adaptamos a aula para jardins, plays, áreas comuns ou até espaços internos amplos.',
    },
    {
      question: 'O professor leva o material?',
      answer:
        'Sim, levamos todo o material necessário: bolas, cones, traves portáteis, coletes, etc.',
    },
    {
      question: 'Posso formar um grupo com vizinhos?',
      answer:
        'Sim! Essa é uma ótima opção para reduzir o custo por aluno e promover a socialização.',
    },
    {
      question: 'Como sei se o professor é confiável?',
      answer:
        'No SuperFut, todos os professores passam por rigorosa checagem de antecedentes e treinamento pedagógico.',
    },
    {
      question: 'Tem aula em dias de chuva?',
      answer:
        'Se o local for descoberto, podemos adaptar para uma área coberta do condomínio ou reagendar a aula.',
    },
    {
      question: 'Qual a frequência recomendada?',
      answer:
        'Para iniciação, 1 a 2 vezes por semana é o ideal para criar rotina sem sobrecarregar a criança.',
    },
    {
      question: 'Vocês atendem em toda São Paulo?',
      answer:
        'Atendemos as principais zonas (Sul, Oeste, Centro). Consulte disponibilidade para seu bairro.',
    },
    {
      question: 'E se meu filho não gostar?',
      answer:
        'Oferecemos uma aula experimental justamente para você testar a adaptação antes de contratar um plano.',
    },
    {
      question: 'Como funciona o pagamento?',
      answer:
        'Trabalhamos com planos mensais ou pacotes de aulas, com pagamento facilitado via Pix ou cartão.',
    },
  ],
  content: (
    <div className="space-y-8 text-slate-700 leading-relaxed">
      <div className="prose prose-slate max-w-none">
        <p className="text-xl">
          A vida em São Paulo é corrida. Entre trabalho, escola e trânsito,
          encontrar tempo para levar os filhos às atividades extracurriculares é
          um desafio. Nesse cenário, as{' '}
          <strong>aulas de futebol a domicílio</strong> surgem como uma
          tendência forte. Mas será que vale a pena trocar a escolinha
          tradicional pelo personal soccer?
        </p>
      </div>

      <section>
        <h2 className="text-2xl font-display font-bold text-super-text mb-6">
          Comparativo: Domicílio vs Escolinha
        </h2>
        <div className="border rounded-xl overflow-hidden shadow-sm bg-white">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-[150px] font-bold">Aspecto</TableHead>
                <TableHead className="font-bold text-super-blue">
                  Aula a Domicílio (SuperFut)
                </TableHead>
                <TableHead className="font-bold text-slate-500">
                  Escolinha Tradicional
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Deslocamento</TableCell>
                <TableCell className="text-green-600 font-semibold">
                  Zero (Nós vamos até você)
                </TableCell>
                <TableCell>Necessário (Trânsito/Estacionamento)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Atenção do Prof.</TableCell>
                <TableCell className="text-green-600 font-semibold">
                  100% focada ou pequenos grupos
                </TableCell>
                <TableCell>Dividida entre 15-20 alunos</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Segurança</TableCell>
                <TableCell className="text-green-600 font-semibold">
                  Ambiente do seu condomínio
                </TableCell>
                <TableCell>Ambiente externo público/privado</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Flexibilidade</TableCell>
                <TableCell className="text-green-600 font-semibold">
                  Você escolhe o horário
                </TableCell>
                <TableCell>Horários de grade fixa</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Socialização</TableCell>
                <TableCell>Com vizinhos e amigos próximos</TableCell>
                <TableCell>Com crianças diversas</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            Vantagens do Modelo a Domicílio
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Conveniência inigualável:</strong> O tempo que você
              gastaria no trânsito pode ser usado para trabalhar, descansar ou
              ficar com a família.
            </li>
            <li>
              <strong>Segurança:</strong> Em tempos incertos, manter seu filho
              dentro do ambiente controlado do condomínio traz paz de espírito.
            </li>
            <li>
              <strong>Evolução mais rápida:</strong> Com atenção
              individualizada, o professor corrige movimentos e adapta o treino
              especificamente para a necessidade do seu filho.
            </li>
          </ul>
        </div>

        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 flex gap-4">
          <Info className="w-8 h-8 text-super-blue flex-shrink-0" />
          <div>
            <h3 className="font-bold text-super-blue text-lg">
              Sobre o Investimento
            </h3>
            <p className="text-sm">
              Embora a hora-aula individual seja mais alta que uma mensalidade
              de escolinha, ao colocar na ponta do lápis os gastos com
              combustível, estacionamento e, principalmente,{' '}
              <strong>o valor do seu tempo</strong>, a aula a domicílio se
              mostra extremamente competitiva, especialmente se feita em
              pequenos grupos de vizinhos (rateio).
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-super-text mb-3">
            Para quem é indicado?
          </h2>
          <p>O modelo SuperFut Kids é ideal para:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Pais com agenda cheia que valorizam otimização de tempo.</li>
            <li>
              Crianças tímidas que se sentem intimidadas em turmas grandes.
            </li>
            <li>Condomínios com boa infraestrutura (quadra ou área verde).</li>
            <li>Grupos de amigos que querem uma atividade exclusiva.</li>
          </ul>
        </div>
      </section>

      <section className="bg-slate-900 text-white p-8 rounded-3xl text-center shadow-xl">
        <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
          Faça as contas e escolha o melhor!
        </h2>
        <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
          Que tal experimentar a comodidade de ter um professor especializado
          tocando a campainha da sua casa? Agende uma aula experimental e veja a
          diferença.
        </p>
        <Button
          size="lg"
          className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
          asChild
        >
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-6 h-6 mr-2" />
            Cotar Aulas no Meu Condomínio
          </a>
        </Button>
      </section>
    </div>
  ),
}
